// components/Courses/CourseCard.jsx
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Paper,
  Stack,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useTranslation } from "react-i18next";

const BRAND = "#253e66";

const Card = styled(Paper)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
  transition: "transform 120ms ease, box-shadow 120ms ease",
  height: "100%",
  "&:hover": {
    transform: "translateY(-1px)",
    boxShadow: "0 12px 28px rgba(0,0,0,0.12)",
  },
  [theme.breakpoints.down("sm")]: { flexDirection: "column" },
}));

const LeftRail = styled(Box)(({ theme }) => ({
  flexBasis: "34%",
  minWidth: 180,
  background: "linear-gradient(135deg, #253e66 0%, #1b2f4a 100%)",
  color: "#fff",
  padding: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  [theme.breakpoints.down("sm")]: {
    flexBasis: "100%",
    minWidth: "100%",
  },
}));

const RightPane = styled(Box)(({ theme }) => ({
  flex: 1,
  background: "#fff",
  padding: theme.spacing(3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  minWidth: 0,
}));

const PillButton = styled(Button)(({ theme }) => ({
  borderRadius: 999,
  textTransform: "none",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const LogoBox = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 12,
  background: alpha(BRAND, 0.04),
  border: `1px solid ${alpha(BRAND, 0.12)}`,
}));

const CourseCard = ({ course, onClick, variant = "default", cardSx }) => {
  const { t, i18n } = useTranslation("courses");
  const isEn = String(i18n.resolvedLanguage || i18n.language || "es")
    .toLowerCase()
    .startsWith("en");
  const pick = (es, en) => (isEn ? en ?? es : es);

  const compact = variant === "compact";

  const {
    title,
    titleEN,
    description,
    descriptionEN,
    progress,
    currentModule,
    currentModuleEN,
    assigned,
    link,
    logo,
  } = course;

  const titleToShow = pick(title, titleEN);
  const descriptionToShow = pick(description, descriptionEN);
  const currentModuleToShow = pick(currentModule, currentModuleEN);

  const handleAction = () =>
    assigned ? onClick() : window.open(link, "_blank", "noopener,noreferrer");

  const imgSrc =
    logo && (logo.startsWith("data:") ? logo : `data:image/png;base64,${logo}`);
  const completed = progress?.completed ?? 0;
  const total = progress?.total ?? 0;
  const pct = progress?.percentage ?? 0;

  // 👇 NUEVO: no empezado si assigned y progreso 0
  const isNotStarted =
    assigned && completed === 0 && (pct === 0 || !Number.isFinite(pct));
  const actionLabel = assigned
    ? isNotStarted
      ? t("start")
      : t("continue")
    : t("enroll");

  return (
    <Card
      sx={{
        ...(compact && {
          borderRadius: 12,
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        }),
        ...cardSx,
      }}
    >
      <LeftRail
        sx={
          compact
            ? {
                flexBasis: "30%",
                minWidth: 140,
                p: 1.25, // ↓ antes 1.5
              }
            : undefined
        }
      >
        <Stack spacing={0.25}>
          {" "}
          {/* ↓ antes 0.5 */}
          <Typography
            sx={{
              fontSize: compact ? 13 : 16,
              fontWeight: 600,
              lineHeight: 1.15,
            }}
          >
            {/* ↓ font 14→13  y lineHeight 1.2→1.15 */}
            {titleToShow}
          </Typography>
          <Button
            variant="text"
            onClick={handleAction}
            sx={{
              alignSelf: "flex-start",
              color: "rgba(255,255,255,0.9)",
              fontSize: compact ? 10.5 : 12, // ↓ antes 11
              pl: 0,
              minHeight: 28, // (añadido) asegura botón más bajo
              "&:hover": { color: "#fff" },
            }}
            endIcon={<ChevronRightIcon fontSize="small" />}
          >
            {t("viewModules")}
          </Button>
        </Stack>

        {imgSrc && (
          <LogoBox
            sx={
              compact ? { width: 52, height: 52, borderRadius: 10 } : undefined
            }
          >
            {/* ↓ antes 56x56 / 44x44 → 40x40 */}
            <Box
              component="img"
              src={imgSrc}
              alt={`${titleToShow} logo`}
              sx={{
                width: compact ? 40 : 56,
                height: compact ? 40 : 56,
                objectFit: "contain",
                display: "block",
              }}
            />
          </LogoBox>
        )}
      </LeftRail>

      <RightPane sx={compact ? { p: 1.5, gap: 1 } : undefined}>
        {" "}
        {/* ↓ antes p:2, gap:1.25 */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          gap={1.5}
          sx={{ minWidth: 0 }}
        >
          {/* ↓ gap 2→1.5 */}
          <Typography
            sx={{
              color: "#6f7a86",
              fontSize: compact ? 11.5 : 13, // ↓ antes 12
              letterSpacing: 0.3,
              display: "-webkit-box",
              WebkitLineClamp: 1,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
            title={currentModuleToShow}
          >
            {currentModuleToShow}
          </Typography>

          <Stack
            spacing={0.25}
            alignItems="flex-end"
            sx={{ width: { xs: "100%", sm: compact ? 168 : 220 }, minWidth: 0 }}
          >
            {/* ↓ spacing 0.5→0.25  y width 180→168 */}
            <Typography
              sx={{ fontSize: compact ? 10.5 : 12, color: "#8a8fa3" }}
            >
              {t("modulesProgress", { completed, total })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={pct}
              sx={{
                height: compact ? 5 : 8, // ↓ antes 6
                borderRadius: 999,
                backgroundColor: "#eef2f6",
                "& .MuiLinearProgress-bar": { backgroundColor: BRAND },
                width: "100%",
              }}
            />
          </Stack>
        </Stack>
        {descriptionToShow && (
          <Typography
            variant="subtitle1"
            sx={{
              color: "text.primary",
              fontWeight: 500,
              display: "-webkit-box",
              WebkitLineClamp: compact ? 1 : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minHeight: compact ? 0 : 48,
              fontSize: compact ? 12.5 : undefined, // ↓ antes 13
            }}
            title={descriptionToShow}
          >
            {descriptionToShow}
          </Typography>
        )}
        <Box
          mt="auto"
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}
          sx={{ flexWrap: "wrap", pt: 0.125 }}
        >
          {/* ↓ gap 1.5→1  y pt 0.25→0.125 */}
          <PillButton
            variant="contained"
            onClick={handleAction}
            size={compact ? "small" : "medium"}
            sx={{
              backgroundColor: assigned ? BRAND : "#27aae1",
              "&:hover": { backgroundColor: assigned ? "#1b2f4a" : "#0f76a3" },
              px: compact ? 1.25 : 2, // ↓ antes 1.5
              minHeight: 30, // (añadido) botón un poco más bajo
            }}
          >
            {actionLabel}
          </PillButton>
        </Box>
      </RightPane>
    </Card>
  );
};

CourseCard.propTypes = {
  variant: PropTypes.oneOf(["default", "compact"]),
  cardSx: PropTypes.object, // 👈 nuevo
  course: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    titleEN: PropTypes.string,
    description: PropTypes.string,
    descriptionEN: PropTypes.string,
    currentModule: PropTypes.string,
    currentModuleEN: PropTypes.string,
    assigned: PropTypes.bool,

    progress: PropTypes.shape({
      completed: PropTypes.number,
      total: PropTypes.number,
      percentage: PropTypes.number,
    }),

    link: PropTypes.string,
    logo: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

export default CourseCard;
